import {ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren} from '@angular/core';
import {
  SynopticConfigurationComponent,
  SynopticElementAction
} from '../../../../../../model/synoptics/synoptic-configuration-types';
import {SynopticFile} from '../../../../../../model/synoptics/synoptic-file';
import {Device} from '../../../../../../model/device';
import {Kpi} from '../../../../../../model/kpi';
import {AssetMeasurement} from '../../../../../../model/asset-measurement';
import {DeviceMeasurement} from '../../../../../../model/device-measurement';
import {CommandsSchema, CommandsSchemas} from '@iotsens/commands';
import {ShowTooltipArgs, TooltipRow} from '../../../../../../model/synoptics/synoptic-arg-types';
import {SelectItem} from 'primeng/api';
import {DevicesService} from '../../../../../../services/devices.service';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {SynopticsService} from '../../../../../../services/synoptics.service';
import {FormGroup} from '@angular/forms';
import {SynopticFilters} from '../../../../../../model/filters/synoptic-filters';
import {SynopticsResponse} from '../../../../../../model/responses/synoptics-response';
import {Synoptic} from '../../../../../../model/synoptics/synoptic';
import {
  GenericCommandInputComponent
} from '../../../../../shared/generic-command-input/generic-command-input.component';
import {combineLatest, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

@Component({
  selector: 'app-synoptics-form-event-action',
  templateUrl: './synoptics-form-event-action.component.html',
  styleUrls: ['./synoptics-form-event-action.component.scss']
})
export class SynopticsFormEventActionComponent implements OnInit {
  @ViewChildren(GenericCommandInputComponent) viewCommandInputs!: QueryList<GenericCommandInputComponent>;

  public action: SynopticElementAction;
  public synopticFiles: SynopticFile[];
  public synopticInfoForm: FormGroup;
  public deviceMeasurements: DeviceMeasurement[];
  public assetMeasurements: AssetMeasurement[];
  public kpis: Kpi[];
  public devices: Device[];
  public uuidDeviceMap: Map<string, string>;
  public uuidSensorMap: Map<string, string>;
  public commandsSchemaMap: Map<string, CommandsSchemas>;
  public components: SynopticConfigurationComponent[];
  public synopticUuid?: string;

  public availableCommandSchemas: CommandsSchema[];
  public selectedCommandSchema: CommandsSchema;
  public selectedCommandParams: any;
  public selectedComponent: SynopticConfigurationComponent;
  public rowBeingModified: TooltipRow;
  public paramOptions: SelectItem[];
  public devicesFirst: number;
  public devicesRows: number;
  public synoptics: Synoptic[];

  industrialFormValidity: boolean;
  isFormValid$: Observable<boolean>;

  constructor(
    public dynamicDialogRef: DynamicDialogRef,
    public changeDetectorRef: ChangeDetectorRef,
    public config: DynamicDialogConfig,
    private devicesService: DevicesService,
    private synopticsService: SynopticsService,
    private cdr: ChangeDetectorRef
  ) {
    this.paramOptions = [
      {label: 'misc.synopticParamOptions.fixed', value: false},
      {label: 'misc.synopticParamOptions.askUser', value: true}
    ];
    this.devicesFirst = 0;
    this.devicesRows = 5;
  }

  ngOnInit(): void {
    this.loadInputs(this.config.data);
    if (this.action.type === 'executeCommand') {
      if (this.action.args.deviceUuid) {
        this.loadDeviceSchemas(this.action.args.deviceUuid).then(() => {
          if (this.action.args.schema) {
            this.onCommandSchemaChange(this.action.args.schema);
            if (this.action.args.command) {
              this.onCommandChange(this.action.args.command);
            }
          }
        });
      }
    } else if (this.action.type === 'linkToSynoptic') {
      this.loadSynoptics();
    }
  }

  public onCommandSchemaChange(schemaName: string): void {
    this.selectedCommandSchema = this.availableCommandSchemas.find(cs => cs.name === schemaName);
  }

  public onCommandChange(commandName: string): void {
    for (let key of Object.keys(this.selectedCommandSchema.commands)) {
      if (key === this.action.args.command) {
        this.selectedCommandParams = this.selectedCommandSchema.commands[key]['params'];
        this.action.args.params = this.action.args.params ?? {};
        const params = {};
        for (let key of Object.keys(this.selectedCommandParams)) {
          params[key] = this.action.args.params[key] ?? null;
        }
        this.action.args.params = params;
      }
    }
    this.observeCommandInputs();
  }

  public observeCommandInputs(): void {
    this.cdr.detectChanges();

    const inputValidations$: Observable<boolean>[] = this.viewCommandInputs.map((input: GenericCommandInputComponent) => input.isValid$);
    this.isFormValid$ = combineLatest(inputValidations$).pipe(
      map((validations: boolean[]) => validations.every((isValid: boolean) => isValid))
    );
    this.cdr.detectChanges();
  }

  public applyDeviceToArgs(deviceUuid: string): void {
    this.action.args.deviceUuid = deviceUuid;
    this.loadDeviceSchemas(deviceUuid);
  }

  public addTooltipRow(rows: TooltipRow[]): void {
    rows.push({label: '', value: null});
  }

  public removeTooltipRow(row: TooltipRow, args: ShowTooltipArgs): void {
    args.rows = args.rows.filter(r => r != row);
  }

  private loadDeviceSchemas(deviceUuid: string): Promise<void> {
    if (this.commandsSchemaMap.has(deviceUuid)) {
      this.availableCommandSchemas = this.commandsSchemaMap.get(deviceUuid).commandSchemas;
      return Promise.resolve();
    } else {
      return this.devicesService.getDeviceCommandSchemasByUuid(deviceUuid).toPromise().then(response => {
        this.commandsSchemaMap.set(deviceUuid, response);
        this.availableCommandSchemas = response.commandSchemas;
      });
    }
  }

  private loadSynoptics(): void {
    const filters = new SynopticFilters();
    filters.tenantUuids = [this.synopticInfoForm.get('tenant').value.uuid];
    const organizationUuid = this.synopticInfoForm.get('organization')?.value?.uuid;
    filters.organizationUuids = organizationUuid ? [organizationUuid] : null;
    this.synopticsService.getSynoptics(null, null, filters).toPromise().then((response: SynopticsResponse) => {
      this.synoptics = response.synoptics
        .filter(s => s.uuid !== this.synopticUuid)
        .filter(s => this.synopticInfoForm.get('tenant').value.uuid === s.tenant.uuid);
      let organization = this.synopticInfoForm.get('organization').value;
      if (organization) {
        this.synoptics.filter(s => organization.uuid === s.organization.uuid);
      }
    });
  }

  private loadInputs(data: any): void {
    this.action = data.action;
    this.synopticFiles = data.synopticFiles;
    this.synopticInfoForm = data.synopticInfoForm;
    this.deviceMeasurements = data.deviceMeasurements;
    this.assetMeasurements = data.assetMeasurements;
    this.kpis = data.kpis;
    this.devices = data.devices;
    this.uuidDeviceMap = data.uuidDeviceMap;
    this.uuidSensorMap = data.uuidSensorMap;
    this.commandsSchemaMap = data.commandsSchemaMap;
    this.components = data.components;
    this.synopticUuid = data.synopticUuid;
    this.changeDetectorRef.detectChanges();
  }

  get isValid(): boolean {
    switch (this.action.type) {
      case 'linkToSynoptic':
        return this.action.args.synopticUuid;
      case 'executeCommand':
        return this.action.args.command;
      default:
        return true;
    }
  }

}
